import { ProductType as CourseType } from "@/common/course/types";
import { ProgramType } from "@/common/program/types";
import {
  localizeTabDefaults,
  productTabContentKeys,
} from "../../shared/productCardListMessages";
import type { ProductCardTabsByTopicProps, TabHighlightItem } from "./types";

const isHighlightItem = (value: unknown): value is TabHighlightItem =>
  typeof value === "object" &&
  value !== null &&
  typeof (value as TabHighlightItem).icon === "string" &&
  typeof (value as TabHighlightItem).title === "string" &&
  typeof (value as TabHighlightItem).description === "string";

/**
 * Validate the per-tab data override. Returns `null` (caller falls back
 * to localized defaults) when the value is missing, empty, or any
 * item is malformed. WordPress authors supply this via nested
 * `[product-card-tabs-by-topic-item]` shortcodes; Storybook / direct
 * invocations supply it as a plain array.
 */
const sanitizeTabData = (
  value: TabHighlightItem[] | undefined,
): TabHighlightItem[] | null => {
  if (!value || !Array.isArray(value) || value.length === 0) return null;
  return value.every(isHighlightItem) ? value : null;
};

type Overrides = Pick<
  ProductCardTabsByTopicProps,
  | "certSubtitle"
  | "coursesSubtitle"
  | "eeSubtitle"
  | "mastersSubtitle"
  | "bachelorsSubtitle"
  | "doctorateSubtitle"
  | "certData"
  | "coursesData"
  | "eeData"
  | "mastersData"
  | "bachelorsData"
  | "doctorateData"
>;

const SUBTITLE_KEY: Record<string, keyof Overrides> = {
  [ProgramType.CERTIFICATE]: "certSubtitle",
  [CourseType.COURSE]: "coursesSubtitle",
  [CourseType.EXECUTIVE_EDUCATION]: "eeSubtitle",
  [ProgramType.MASTERS]: "mastersSubtitle",
  [ProgramType.BACHELORS]: "bachelorsSubtitle",
  [ProgramType.DOCTORATE]: "doctorateSubtitle",
};

const DATA_KEY: Record<string, keyof Overrides> = {
  [ProgramType.CERTIFICATE]: "certData",
  [CourseType.COURSE]: "coursesData",
  [CourseType.EXECUTIVE_EDUCATION]: "eeData",
  [ProgramType.MASTERS]: "mastersData",
  [ProgramType.BACHELORS]: "bachelorsData",
  [ProgramType.DOCTORATE]: "doctorateData",
};

type Translate = (key: string) => string;

/**
 * Resolve the subtitle and value-prop block for a given product type,
 * preferring CMS-supplied overrides over localized defaults.
 */
export const resolveTabContent = (
  productType: string,
  overrides: Overrides,
  t: Translate,
): { subtitle: string; data: TabHighlightItem[] } => {
  const defaults = localizeTabDefaults(productType, t);
  const overrideSubtitle = overrides[SUBTITLE_KEY[productType]] as
    | string
    | undefined;
  const overrideData = sanitizeTabData(
    overrides[DATA_KEY[productType]] as TabHighlightItem[] | undefined,
  );

  return {
    subtitle:
      overrideSubtitle && overrideSubtitle.trim().length > 0
        ? overrideSubtitle
        : defaults.subtitle,
    data: overrideData ?? defaults.data,
  };
};

/**
 * Render a heading template with `{title}` / `{category}` placeholders.
 * Callers should pass a CMS override or an already-localized default template.
 */
export const resolveHeading = (
  template: string,
  title: string | undefined,
  category: string,
): string =>
  template
    .replaceAll("{title}", title ?? "")
    .replaceAll("{category}", category)
    .replace(/\s+/g, " ")
    .trim();

export const getCategoryTitleKey = (productType: string): string | undefined =>
  productTabContentKeys[productType]?.titleKey;
